import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, SUB_CONFIRM_LAYER, useConfirmDialog } from '@safetyhub/ui';
import {
  adminApi,
  FeatureFlagGroupDetail,
  FeatureFlagSearchTarget,
} from '../../lib/api';
import { useToastStore } from '../../stores/toast';

interface GroupDetailModalProps {
  groupId: string;
  onClose: () => void;
}

export default function GroupDetailModal({ groupId, onClose }: GroupDetailModalProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'members' | 'flags'>('members');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'feature-flag-groups', groupId],
    queryFn: () => adminApi.getFeatureFlagGroup(groupId),
  });

  const group = data?.data as FeatureFlagGroupDetail | undefined;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flag-groups', groupId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flag-groups'] });
  };

  // 부모가 열 때만 마운트하므로 open은 늘 true다. 높이 제한·본문 스크롤은 base `bodyScroll`(기본)이
  // 맡는다 — 종전 패널의 `max-h-[80vh] flex-col` + 본문 `overflow-auto`와 같은 일이다(탭 줄은 이제
  // 본문과 함께 스크롤된다). 멤버 검색 결과 목록(absolute)은 종전에도 본문 스크롤러 안이라 클리핑
  // 조건이 같다.
  return (
    <Modal
      open
      onClose={onClose}
      title={group?.name ?? '로딩 중...'}
      size="xl"
    >
      {group?.description && <p className="mb-3 text-sm text-gray-500">{group.description}</p>}

      {/* 탭 */}
      <div className="mb-4 border-b flex gap-4">
        <button
          onClick={() => setActiveTab('members')}
          className={`py-3 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'members'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          멤버 ({group?.memberCount ?? 0})
        </button>
        <button
          onClick={() => setActiveTab('flags')}
          className={`py-3 text-sm font-medium border-b-2 -mb-px ${
            activeTab === 'flags'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          연결된 플래그 ({group?.flagLinkCount ?? 0})
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">로딩 중...</div>
      ) : activeTab === 'members' ? (
        <MembersTab group={group!} onInvalidate={invalidate} />
      ) : (
        <FlagsTab group={group!} />
      )}
    </Modal>
  );
}

function MembersTab({
  group,
  onInvalidate,
}: {
  group: FeatureFlagGroupDetail;
  onInvalidate: () => void;
}) {
  const showToast = useToastStore((s) => s.showToast);
  // 멤버 제거 확인 — 그룹 상세(z-[70]) 위에 뜨므로 한 층 위(`SUB_CONFIRM_LAYER`).
  const [confirm, confirmDialog] = useConfirmDialog();

  const addMutation = useMutation({
    mutationFn: (userIds: string[]) =>
      adminApi.addFeatureFlagGroupMembers(group.id, userIds),
    onSuccess: (result) => {
      if (result.success) {
        onInvalidate();
        showToast('멤버가 추가되었습니다.', 'success');
      } else {
        showToast(result.error?.message || '추가에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('멤버 추가 중 오류가 발생했습니다.', 'error');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      adminApi.removeFeatureFlagGroupMember(group.id, userId),
    onSuccess: (result) => {
      if (result.success) {
        onInvalidate();
        showToast('멤버가 제거되었습니다.', 'success');
      }
    },
    onError: () => {
      showToast('멤버 제거 중 오류가 발생했습니다.', 'error');
    },
  });

  return (
    <div className="space-y-4">
      {/* 멤버 검색/추가 */}
      <MemberSearchInput
        onSelect={(target) => addMutation.mutate([target.id])}
        isAdding={addMutation.isPending}
      />

      {/* 멤버 목록 */}
      {group.members.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          아직 멤버가 없습니다. 위에서 사용자를 검색하여 추가해주세요.
        </div>
      ) : (
        <div className="divide-y">
          {group.members.map((m) => (
            <div key={m.id} className="py-2 flex items-center justify-between">
              <div>
                <span className="font-medium text-gray-900 text-sm">
                  {m.userName ?? '(알 수 없음)'}
                </span>
                {m.userEmail && (
                  <span className="ml-2 text-gray-500 text-xs">{m.userEmail}</span>
                )}
              </div>
              <button
                onClick={async () => {
                  if (
                    await confirm({
                      title: '멤버 제거',
                      body: `${m.userName ?? '이 사용자'}를 그룹에서 제거하시겠습니까?`,
                      confirmLabel: '제거',
                      danger: true,
                      ...SUB_CONFIRM_LAYER,
                    })
                  ) {
                    removeMutation.mutate(m.userId);
                  }
                }}
                disabled={removeMutation.isPending}
                className="text-red-600 hover:text-red-800 text-xs disabled:opacity-50"
              >
                제거
              </button>
            </div>
          ))}
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

function MemberSearchInput({
  onSelect,
  isAdding,
}: {
  onSelect: (target: FeatureFlagSearchTarget) => void;
  isAdding: boolean;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FeatureFlagSearchTarget[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await adminApi.searchFeatureFlagTargets('USER', q);
      if (res.success && res.data) {
        setResults(res.data);
        setIsOpen(res.data.length > 0);
      }
    } catch {
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setIsOpen(true)}
        placeholder="멤버 추가: 이름 또는 이메일 검색 (2글자 이상)"
        disabled={isAdding}
        className="w-full px-3 py-2 border border-gray-300 rounded-control text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
      />
      {isLoading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-red-500 rounded-full animate-spin" />
        </div>
      )}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-card shadow-lg max-h-48 overflow-auto">
          {results.map((t) => (
            <button
              key={`${t.label}-${t.id}`}
              type="button"
              onClick={() => {
                onSelect(t);
                setQuery('');
                setIsOpen(false);
                setResults([]);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-sm"
            >
              {/* 대상 타입 범례(관리자=red·사용자=blue) — OverrideList의 LABEL_MAP과 같은 체계라 유지. */}
              <span
                className={`px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${
                  t.label === 'superAdmin'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-blue-100 text-blue-700'
                }`}
              >
                {t.label === 'superAdmin' ? '관리자' : '사용자'}
              </span>
              <span className="font-medium truncate">{t.name}</span>
              {t.email && (
                <span className="text-gray-500 truncate">{t.email}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FlagsTab({ group }: { group: FeatureFlagGroupDetail }) {
  if (group.flagLinks.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 text-sm">
        연결된 플래그가 없습니다. 피처 플래그 상세에서 그룹을 연결할 수 있습니다.
      </div>
    );
  }

  return (
    <div className="divide-y">
      {group.flagLinks.map((fl) => (
        <div key={fl.id} className="py-2 flex items-center justify-between">
          <div>
            <span className="font-mono text-sm text-gray-800">{fl.flagKey}</span>
            <span className="ml-2 text-gray-500 text-xs">{fl.flagName}</span>
          </div>
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${
              fl.value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {String(fl.value)}
          </span>
        </div>
      ))}
    </div>
  );
}
